import { useEffect, useRef, useState } from "react";
import { api, fmtEur, fmtDate, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatoBadge } from "./Sales";
import { UploadCloud, Trash2, Loader2 } from "lucide-react";

export default function Sibill() {
  const [invoices, setInvoices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extra = data.skipped ? ` (${data.skipped} duplicati saltati)` : "";
      toast.success(`File Sibill importato: ${data.imported} fatture${extra}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const changeStatus = async (inv, stato) => {
    await api.put(`/invoices/${inv.id}`, { stato_pagamento: stato, nota: inv.nota || "" });
    toast.success("Stato aggiornato"); load();
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa fattura?")) return;
    await api.delete(`/invoices/${id}`); load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Import Sibill / Fatture</h1>
        <p className="text-sm text-slate-500 mt-1">Importa i flussi di cassa dei partner da file CSV o Excel esportato da Sibill.</p>
      </div>

      <Card
        className={`border-2 border-dashed transition-colors ${drag ? "border-dusty-500 bg-dusty-50" : "border-slate-300 bg-white"}`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        data-testid="sibill-dropzone"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          {uploading ? <Loader2 className="h-10 w-10 text-dusty-500 animate-spin" /> : <UploadCloud className="h-10 w-10 text-dusty-500" />}
          <p className="mt-3 font-medium text-slate-700">Trascina qui il file Sibill</p>
          <p className="text-sm text-slate-400">CSV o Excel — colonne riconosciute automaticamente</p>
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="sibill-file-input" />
          <Button className="mt-4 bg-dusty-600 hover:bg-dusty-700 text-white" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="sibill-upload-btn">
            Seleziona file
          </Button>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-100/70 border-b border-slate-200">
                <th className="py-3 px-4">Data</th>
                <th className="py-3 px-4">Descrizione</th>
                <th className="py-3 px-4">Partner</th>
                <th className="py-3 px-4 text-right">Importo</th>
                <th className="py-3 px-4">Stato</th>
                <th className="py-3 px-4">Aggiorna</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="py-3 px-4 text-slate-600 whitespace-nowrap">{fmtDate(inv.data)}</td>
                  <td className="py-3 px-4 text-slate-700 max-w-xs truncate">{inv.descrizione || "—"}</td>
                  <td className="py-3 px-4 text-slate-600">{inv.partner_nome || "—"}</td>
                  <td className="py-3 px-4 text-right font-medium text-slate-800">{fmtEur(inv.importo)}</td>
                  <td className="py-3 px-4"><StatoBadge stato={inv.stato_pagamento} /></td>
                  <td className="py-3 px-4">
                    <Select value={inv.stato_pagamento} onValueChange={(v) => changeStatus(inv, v)}>
                      <SelectTrigger className="h-8 w-36" data-testid={`invoice-status-${inv.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pagato">Pagato</SelectItem>
                        <SelectItem value="non_pagato">Non Pagato</SelectItem>
                        <SelectItem value="in_sospeso">In Sospeso</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-4">
                    <Button size="icon" variant="ghost" onClick={() => del(inv.id)} data-testid={`delete-invoice-${inv.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-slate-400">Nessuna fattura importata</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
